export const IMAGE_CAROUSEL_MARGIN = 8;

/** Default slide width when computing fluid layout without component dimensions. */
export const IMAGE_CAROUSEL_FALLBACK_SLIDE_WIDTH = 140;

export const FLUID_IMAGE_CAROUSEL_ITEMS_BY_VIEWPORT = {
    xs: 2,
    sm: 3,
    md: 4,
    lg: 5,
    xl: 6,
    xxl: 7,
    auto: 5,
};

/**
 * @param {number | null | undefined} imageWidth
 * @return {boolean}
 */
export const usesImageCarouselDimensions = (imageWidth) =>
    imageWidth != null && imageWidth > 0;

/**
 * @param {number} imageWidth
 * @param {number} margin
 * @return {number}
 */
export const getImageCarouselSlideStride = (imageWidth, margin = IMAGE_CAROUSEL_MARGIN) =>
    imageWidth + margin;

/**
 * Sized mode: scroll when total slide width exceeds the container.
 * @param {number} containerWidth
 * @param {number} imageWidth
 * @param {number} imageCount
 * @param {number} margin
 * @return {boolean}
 */
export const isImageCarouselScrollableByDimensions = (
    containerWidth,
    imageWidth,
    imageCount,
    margin = IMAGE_CAROUSEL_MARGIN,
) => {
    if (!containerWidth || !imageWidth || !imageCount) {
        return false;
    }
    return imageCount * getImageCarouselSlideStride(imageWidth, margin) > containerWidth;
};

/**
 * Fluid mode: scroll when there are more images than fit per viewport row.
 * @param {number} imageCount
 * @param {number} itemsPerView
 * @return {boolean}
 */
export const isImageCarouselScrollable = (imageCount, itemsPerView) =>
    imageCount > itemsPerView;

/**
 * @param {string | undefined} viewportWidth
 * @return {number}
 */
export const getFluidImageCarouselItemsPerView = (viewportWidth) =>
    FLUID_IMAGE_CAROUSEL_ITEMS_BY_VIEWPORT[viewportWidth] ?? FLUID_IMAGE_CAROUSEL_ITEMS_BY_VIEWPORT.auto;

/**
 * Fluid mode without viewport context: derive items from container width.
 * @param {number} containerWidth
 * @param {number} imageCount
 * @param {number} margin
 * @param {number} fallbackSlideWidth
 * @return {number}
 */
export const computeFluidImageCarouselItemsPerView = (
    containerWidth,
    imageCount,
    margin = IMAGE_CAROUSEL_MARGIN,
    fallbackSlideWidth = IMAGE_CAROUSEL_FALLBACK_SLIDE_WIDTH,
) => {
    if (!containerWidth || !imageCount) {
        return 1;
    }
    const slideWidth = fallbackSlideWidth + margin;
    return Math.max(1, Math.floor(containerWidth / slideWidth));
};

/**
 * @param {number} itemsPerView
 * @param {number} margin
 * @return {Record<number, {items: number, margin: number}>}
 */
export const buildImageCarouselResponsive = (itemsPerView, margin = IMAGE_CAROUSEL_MARGIN) => ({
    0: {items: itemsPerView, margin},
});

/**
 * @param {string | undefined} viewportWidth
 * @param {number} margin
 * @return {Record<number, {items: number, margin: number}>}
 */
export const buildFluidImageCarouselResponsive = (viewportWidth, margin = IMAGE_CAROUSEL_MARGIN) => ({
    0: {items: getFluidImageCarouselItemsPerView(viewportWidth), margin},
});

/**
 * @param {any} event
 * @return {{hasNext: boolean, hasPrev: boolean} | null}
 */
export const getImageCarouselNavState = (event) => {
    if (event?.item?.index == null) {
        return null;
    }
    const pageInfo = event?.page ?? {};
    const itemsInfo = event?.item ?? {};
    return {
        hasNext: (itemsInfo.index + pageInfo.size) < itemsInfo.count,
        hasPrev: itemsInfo.index > 0,
    };
};

/**
 * @param {number | null | undefined} imageWidth
 * @param {number | null | undefined} imageHeight
 * @return {{slideStyle: {width: number} | undefined, imageStyle: {width: number | string, height?: number}}}
 */
export const getImageCarouselSlideStyles = (imageWidth, imageHeight) => {
    const sized = usesImageCarouselDimensions(imageWidth);
    return {
        slideStyle: sized ? {width: imageWidth} : undefined,
        imageStyle: {
            width: sized ? imageWidth : '100%',
            ...(imageHeight ? {height: imageHeight} : {}),
        },
    };
};
